// src/admin/pages/compliance/vehicles/AdminComplianceQueue.jsx
// ✅ Auto-search (debounced) — no Search button
// ✅ Uses nested detail route: /admin/compliance/vehicles/:regNo
// ✅ UPDATED: Removed "Pending Review" tab
// ✅ UPDATED: Pagination footer now matches OpsRides style:
//    - px-5 py-3 border-t border-slate-200 bg-white
//    - Left hint text-xs text-slate-500
//    - Right controls grouped (View dropdown w/ ▼, Page X of Y, Previous/Next buttons)
//    - Same responsive layout: flex-col gap-3 md:flex-row md:items-center md:justify-between

import React, { useEffect, useMemo, useState } from "react";
import { useNavigate } from "react-router-dom";
import { listAllComplianceQueue, listComplianceQueue } from "../../../../api/adminComplianceApi";

/* --------------------- constants --------------------- */

const STATUS_TABS = [
  { key: "ALL", label: "All" },
  { key: "ACTION_REQUIRED", label: "Action Required" },
  { key: "DRAFT", label: "Draft" },
  { key: "APPROVED_VERIFIED", label: "Approved" },
];

const BUCKETS = [
  { key: "ALL", label: "All" },
  { key: "EXCEPTIONS", label: "Exceptions" },
  { key: "AUTO_APPROVED", label: "Auto Approved" },
];

const SERVICE_TABS = [
  { key: "ALL", label: "All Vehicles" },
  { key: "CAR", label: "Car" },
  { key: "AUTO", label: "Auto" },
  { key: "E_RICKSHAW", label: "E Rikshaw" },
  { key: "VAN", label: "Van" },
];

const PAGE_SIZE_OPTIONS = [10, 25, 50, 100];

const DEFAULT_PAGE_SIZE = 25;
const SEARCH_DEBOUNCE_MS = 350;
const EXPORT_COLUMNS = [
  { key: "registrationNumber", label: "Registration Number" },
  { key: "vehicleType", label: "Vehicle Type" },
  { key: "brand", label: "Brand" },
  { key: "model", label: "Model" },
  { key: "ownerName", label: "Owner" },
  { key: "ownerPhone", label: "Phone" },
  { key: "status", label: "Status" },
  { key: "complianceComplete", label: "Compliance Complete" },
  { key: "complianceVerified", label: "Compliance Verified" },
  { key: "updatedAt", label: "Updated At" },
  { key: "chassisNumber", label: "Chassis Number" },
  { key: "permitNumber", label: "Permit Number" },
];
const DEFAULT_EXPORT_COLUMNS = [
  "registrationNumber",
  "vehicleType",
  "brand",
  "model",
  "chassisNumber",
  "permitNumber",
];

/* --------------------- tiny utils --------------------- */

function classNames(...xs) {
  return xs.filter(Boolean).join(" ");
}

function safeStr(x) {
  return x == null ? "" : String(x);
}

function up(x) {
  return safeStr(x).trim().toUpperCase();
}

function fmtDT(x) {
  if (!x) return "—";
  const d = new Date(x);
  if (Number.isNaN(d.getTime())) return String(x);
  return new Intl.DateTimeFormat(undefined, {
    day: "2-digit",
    month: "short",
    year: "numeric",
    hour: "numeric",
    minute: "2-digit",
  }).format(d);
}

function csvEscape(v) {
  const s = v == null ? "" : String(v);
  if (/[",\n]/.test(s)) return `"${s.replaceAll('"', '""')}"`;
  return s;
}

function downloadCsv(filename, rows) {
  const blob = new Blob([rows], { type: "text/csv;charset=utf-8" });
  const url = URL.createObjectURL(blob);
  const a = document.createElement("a");
  a.href = url;
  a.download = filename;
  document.body.appendChild(a);
  a.click();
  a.remove();
  URL.revokeObjectURL(url);
}

/** Debounce any value (prevents API spam while typing) */
function useDebouncedValue(value, delayMs = 300) {
  const [debounced, setDebounced] = useState(value);

  useEffect(() => {
    const t = setTimeout(() => setDebounced(value), delayMs);
    return () => clearTimeout(t);
  }, [value, delayMs]);

  return debounced;
}

/* --------------------- UI atoms --------------------- */

function Tag({ label, tone = "neutral", className = "" }) {
  const cls =
    tone === "good"
      ? "border-emerald-200 bg-emerald-50 text-emerald-800"
      : tone === "warn"
      ? "border-amber-200 bg-amber-50 text-amber-800"
      : tone === "bad"
      ? "border-rose-200 bg-rose-50 text-rose-800"
      : "border-slate-200 bg-slate-50 text-slate-700";

  return (
    <span
      className={classNames(
        "inline-flex items-center border px-2 py-1 text-[11px] font-bold uppercase tracking-wider leading-none rounded-md",
        cls,
        className
      )}
    >
      {label}
    </span>
  );
}

function StatusTag({ status }) {
  const s = up(status);
  const tone =
    s === "APPROVED_VERIFIED"
      ? "good"
      : s === "PENDING_ADMIN_REVIEW"
      ? "warn"
      : s === "ACTION_REQUIRED"
      ? "bad"
      : "neutral";
  return <Tag label={s || "—"} tone={tone} />;
}

function VehicleTypeTag({ type }) {
  const value = safeStr(type).trim() || "Car";
  const tone = value === "Auto" ? "warn" : value === "E Rikshaw" ? "good" : value === "Van" ? "info" : "neutral";
  return <Tag label={value} tone={tone} className="normal-case tracking-normal text-[12px]" />;
}

function Button({
  children,
  onClick,
  disabled,
  tone = "soft",
  className = "",
  title,
  type = "button",
}) {
  const cls =
    tone === "dark"
      ? "border-slate-900 bg-slate-900 text-white hover:opacity-95"
      : tone === "ghost"
      ? "border-transparent bg-transparent text-slate-700 hover:bg-slate-50"
      : "border-slate-200 bg-white text-slate-900 hover:bg-slate-50";

  return (
    <button
      type={type}
      title={title}
      onClick={disabled ? undefined : onClick}
      disabled={disabled}
      className={classNames(
        "inline-flex h-10 items-center justify-center gap-2 border px-4 text-sm font-semibold rounded-md",
        "disabled:opacity-60 disabled:cursor-not-allowed",
        cls,
        className
      )}
    >
      {children}
    </button>
  );
}

function SelectField({ value, onChange, children, className = "", title }) {
  return (
    <div className={classNames("relative min-w-0", className)} title={title}>
      <select
        value={value}
        onChange={onChange}
        className={classNames(
          "h-10 w-full appearance-none border bg-white pl-3 pr-10 text-sm font-semibold text-slate-900 outline-none",
          "rounded-md border-slate-200 hover:bg-slate-50 focus:border-slate-300 truncate"
        )}
      >
        {children}
      </select>
      <svg
        viewBox="0 0 24 24"
        className="pointer-events-none absolute right-3 top-1/2 h-4 w-4 -translate-y-1/2 text-slate-500"
        fill="none"
        aria-hidden="true"
      >
        <path
          d="M6 9l6 6 6-6"
          stroke="currentColor"
          strokeWidth="2"
          strokeLinecap="round"
        />
      </svg>
    </div>
  );
}

function IconSearch({ className = "" }) {
  return (
    <svg viewBox="0 0 24 24" className={className} fill="none" aria-hidden="true">
      <path
        d="M10.5 18.5a8 8 0 1 1 0-16 8 8 0 0 1 0 16Z"
        stroke="currentColor"
        strokeWidth="2"
      />
      <path
        d="M16.5 16.5 21 21"
        stroke="currentColor"
        strokeWidth="2"
        strokeLinecap="round"
      />
    </svg>
  );
}

function Section({ title, subtitle, right, children }) {
  return (
    <div className="rounded-xl border border-slate-200 bg-white">
      <div className="flex flex-col gap-3 border-b border-slate-200 px-5 py-4 sm:flex-row sm:items-start sm:justify-between">
        <div className="min-w-0">
          <div className="text-[12px] font-bold uppercase tracking-wider text-slate-500">
            {title}
          </div>
          {subtitle ? (
            <div className="mt-1 text-[13px] font-medium text-slate-500">{subtitle}</div>
          ) : null}
        </div>
        {right ? <div className="shrink-0">{right}</div> : null}
      </div>
      <div className="px-5 py-4">{children}</div>
    </div>
  );
}

/* --------------------- main --------------------- */

export default function AdminComplianceQueue() {
  const nav = useNavigate();

  const [status, setStatus] = useState("ALL");
  const [bucket, setBucket] = useState("ALL");
  const [serviceType, setServiceType] = useState("ALL");

  const [q, setQ] = useState("");
  const debouncedQ = useDebouncedValue(q, SEARCH_DEBOUNCE_MS);

  const [page, setPage] = useState(0);
  const [pageSize, setPageSize] = useState(DEFAULT_PAGE_SIZE);

  const [rows, setRows] = useState([]);
  const [totalPages, setTotalPages] = useState(0);
  const [total, setTotal] = useState(0);

  const [loading, setLoading] = useState(true);
  const [err, setErr] = useState("");
  const [showExportPanel, setShowExportPanel] = useState(false);
  const [exportBusy, setExportBusy] = useState(false);
  const [selectedExportColumns, setSelectedExportColumns] = useState(DEFAULT_EXPORT_COLUMNS);

  const title = useMemo(() => {
    const found = STATUS_TABS.find((x) => x.key === status);
    return found ? found.label : "Compliance";
  }, [status]);

  // reset to page 0 when filters/search/pageSize change
  useEffect(() => {
    setPage(0);
  }, [status, bucket, serviceType, debouncedQ, pageSize]);

  async function load({
    nextPage = page,
    nextStatus = status,
    nextBucket = bucket,
    nextServiceType = serviceType,
    nextQ = debouncedQ,
    nextSize = pageSize,
  } = {}) {
    setLoading(true);
    setErr("");
    try {
      const res = await listComplianceQueue({
        status: nextStatus === "ALL" ? undefined : nextStatus,
        bucket: nextBucket,
        serviceType: nextServiceType === "ALL" ? undefined : nextServiceType,
        q: (nextQ || "").trim() || undefined,
        page: nextPage,
        size: nextSize,
      });

      const content = Array.isArray(res?.content) ? res.content : [];
      const tp = Number(res?.totalPages ?? 0) || 0;
      const te = Number(res?.totalElements ?? content.length ?? 0) || 0;

      setRows(content);
      setTotalPages(tp);
      setTotal(te);

      if (tp > 0 && nextPage >= tp) {
        setPage(tp - 1);
      }
    } catch (e) {
      setErr(e?.message || "Failed to load queue");
      setRows([]);
      setTotalPages(0);
      setTotal(0);
    } finally {
      setLoading(false);
    }
  }

  useEffect(() => {
    load({
      nextPage: page,
      nextStatus: status,
      nextBucket: bucket,
      nextServiceType: serviceType,
      nextQ: debouncedQ,
      nextSize: pageSize,
    });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [status, bucket, serviceType, page, debouncedQ, pageSize]);

  const pageLabel = useMemo(() => {
    if (!total) return "0";
    const from = page * pageSize + 1;
    const to = Math.min(total, (page + 1) * pageSize);
    return `${from}–${to}`;
  }, [page, total, pageSize]);

  const isTyping = q !== debouncedQ;

  const disablePrev = loading || page <= 0;
  const disableNext = loading || totalPages === 0 || page + 1 >= totalPages;

  function toggleExportColumn(key) {
    setSelectedExportColumns((prev) =>
      prev.includes(key) ? prev.filter((x) => x !== key) : [...prev, key]
    );
  }

  async function handleExport() {
    if (selectedExportColumns.length === 0) {
      window.alert("Select at least one column to export.");
      return;
    }

    setExportBusy(true);
    try {
      const exportRows = await listAllComplianceQueue({
        status: status === "ALL" ? undefined : status,
        bucket,
        serviceType: serviceType === "ALL" ? undefined : serviceType,
        q: (debouncedQ || "").trim() || undefined,
      });

      const header = selectedExportColumns
        .map((key) => EXPORT_COLUMNS.find((c) => c.key === key)?.label || key)
        .join(",");

      const lines = exportRows.map((row) =>
        selectedExportColumns
          .map((key) => {
            const value =
              key === "updatedAt"
                ? fmtDT(row?.[key])
                : key === "complianceComplete" || key === "complianceVerified"
                ? row?.[key]
                  ? "YES"
                  : "NO"
                : row?.[key];
            return csvEscape(value);
          })
          .join(",")
      );

      downloadCsv(
        `vehicle-compliance-export-${new Date().toISOString().slice(0, 10)}.csv`,
        [header, ...lines].join("\n")
      );
      setShowExportPanel(false);
    } catch (e) {
      window.alert(e?.message || "Failed to export compliance queue.");
    } finally {
      setExportBusy(false);
    }
  }

  return (
    <div className="min-w-0 space-y-4">
      <Section
        title="Compliance"
        subtitle="Review documents/photos and verify vehicle compliance."
        right={
          <div className="inline-flex items-center gap-2">
            <Tag
              label={title}
              tone={
                status === "APPROVED_VERIFIED"
                  ? "good"
                  : status === "ACTION_REQUIRED"
                  ? "bad"
                  : status === "DRAFT"
                  ? "warn"
                  : "neutral"
              }
            />
          </div>
        }
      >
        <div className="space-y-3">
          <div className="flex flex-wrap gap-2">
            {SERVICE_TABS.map((tab) => {
              const active = serviceType === tab.key;
              return (
                <button
                  key={tab.key}
                  onClick={() => {
                    setServiceType(tab.key);
                    setPage(0);
                  }}
                  className={classNames(
                    "inline-flex h-10 items-center rounded-md border px-4 text-sm font-semibold transition",
                    active
                      ? "border-slate-900 bg-slate-900 text-white"
                      : "border-slate-200 bg-white text-slate-700 hover:bg-slate-50"
                  )}
                >
                  {tab.label}
                </button>
              );
            })}
          </div>

          <div className="flex flex-wrap gap-2">
            {STATUS_TABS.map((s) => {
              const active = status === s.key;
              return (
                <button
                  key={s.key}
                  onClick={() => {
                    setStatus(s.key);
                    setPage(0);
                  }}
                  className={classNames(
                    "inline-flex h-10 items-center rounded-md border px-4 text-sm font-semibold transition",
                    active
                      ? "border-slate-900 bg-slate-900 text-white"
                      : "border-slate-200 bg-white text-slate-700 hover:bg-slate-50"
                  )}
                >
                  {s.label}
                </button>
              );
            })}
          </div>

          <div className="grid grid-cols-1 gap-3 lg:grid-cols-12 lg:items-start">
            <div className="lg:col-span-3">
              <SelectField
                value={bucket}
                onChange={(e) => {
                  setBucket(e.target.value);
                  setPage(0);
                }}
                title="Queue bucket"
              >
                {BUCKETS.map((b) => (
                  <option key={b.key} value={b.key}>
                    {b.label}
                  </option>
                ))}
              </SelectField>
            </div>

            <div className="lg:col-span-6">
              <div className="flex h-10 items-center gap-2 rounded-md border border-slate-200 bg-white px-3 focus-within:border-slate-300">
                <IconSearch className="h-4 w-4 shrink-0 text-slate-500" />
                <input
                  value={q}
                  onChange={(e) => setQ(e.target.value)}
                  placeholder="Search reg no / owner name / phone"
                  className="min-w-0 w-full bg-transparent text-sm font-semibold text-slate-900 outline-none placeholder:text-slate-400"
                />
                {q ? (
                  <button
                    type="button"
                    onClick={() => setQ("")}
                    className="shrink-0 rounded-md px-2 py-1 text-xs font-bold text-slate-600 hover:bg-slate-50"
                    title="Clear"
                  >
                    ✕
                  </button>
                ) : null}
              </div>

              <div className="mt-1 flex items-center justify-between">
                <div className="text-[12px] font-medium text-slate-500">{isTyping ? "Searching…" : "\u00A0"}</div>
                <div className="text-[12px] font-medium text-slate-500">
                  {loading ? "Loading…" : total ? `Showing ${pageLabel} of ${total}` : "No records"}
                </div>
              </div>
            </div>

            <div className="lg:col-span-3 flex items-center justify-start lg:justify-end">
              <div className="inline-flex items-center gap-2">
                <Tag
                  label={`PAGE ${Math.max(1, page + 1)} / ${Math.max(1, totalPages || 1)}`}
                  tone="neutral"
                  className="hidden lg:inline-flex"
                />
              </div>
            </div>
          </div>

          {err ? (
            <div className="rounded-md border border-rose-200 bg-rose-50 px-4 py-3 text-[13px] font-semibold text-rose-700">
              {err}
            </div>
          ) : null}

          {showExportPanel ? (
            <div className="rounded-md border border-slate-200 bg-slate-50 px-4 py-4">
              <div className="flex flex-col gap-3 lg:flex-row lg:items-start lg:justify-between">
                <div>
                  <div className="text-[12px] font-bold uppercase tracking-wider text-slate-500">
                    Export Queue
                  </div>
                  <div className="mt-1 text-[13px] font-medium text-slate-600">
                    Choose the columns to export for the current filters.
                  </div>
                </div>
                <div className="flex items-center gap-2">
                  <Button
                    onClick={() => setSelectedExportColumns(DEFAULT_EXPORT_COLUMNS)}
                    tone="ghost"
                    disabled={exportBusy}
                  >
                    Reset
                  </Button>
                  <Button onClick={handleExport} tone="dark" disabled={exportBusy}>
                    {exportBusy ? "Exporting…" : "Download CSV"}
                  </Button>
                </div>
              </div>

              <div className="mt-4 grid grid-cols-1 gap-2 sm:grid-cols-2 lg:grid-cols-3">
                {EXPORT_COLUMNS.map((col) => {
                  const active = selectedExportColumns.includes(col.key);
                  return (
                    <label
                      key={col.key}
                      className={classNames(
                        "flex items-center gap-3 rounded-md border px-3 py-2 text-sm font-semibold",
                        active
                          ? "border-slate-900 bg-white text-slate-900"
                          : "border-slate-200 bg-white text-slate-600"
                      )}
                    >
                      <input
                        type="checkbox"
                        checked={active}
                        onChange={() => toggleExportColumn(col.key)}
                        className="h-4 w-4 rounded border-slate-300"
                      />
                      <span>{col.label}</span>
                    </label>
                  );
                })}
              </div>
            </div>
          ) : null}
        </div>
      </Section>

      <div className="overflow-hidden rounded-xl border border-slate-200 bg-white">
        <div className="border-b border-slate-200 px-5 py-3">
          <div className="flex flex-col gap-3 sm:flex-row sm:items-center sm:justify-between">
            <div>
              <div className="text-[12px] font-bold uppercase tracking-wider text-slate-500">Queue</div>
              <div className="mt-1 text-[13px] font-medium text-slate-500">
                Export the current filtered table with only the columns you need.
              </div>
            </div>

            <Button onClick={() => setShowExportPanel((v) => !v)} tone="dark" className="w-full sm:w-auto">
              {showExportPanel ? "Hide Export" : "Export Columns"}
            </Button>
          </div>
        </div>

        {loading ? (
          <div className="px-5 py-10 text-sm font-semibold text-slate-600">Loading…</div>
        ) : rows.length === 0 ? (
          <div className="px-5 py-12 text-center">
            <div className="text-sm font-semibold text-slate-900">No results</div>
            <div className="mt-1 text-[13px] font-medium text-slate-500">Try changing status/bucket/search.</div>
          </div>
        ) : (
          <div className="w-full overflow-x-auto">
            <table className="w-full min-w-[980px] table-auto">
              <thead className="bg-slate-50">
                <tr className="border-b border-slate-200">
                  <th className="px-5 py-3 text-left text-[12px] font-bold uppercase tracking-wider text-slate-500">
                    Reg No
                  </th>
                  <th className="px-5 py-3 text-left text-[12px] font-bold uppercase tracking-wider text-slate-500">
                    Vehicle Type
                  </th>
                  <th className="px-5 py-3 text-left text-[12px] font-bold uppercase tracking-wider text-slate-500">
                    Owner
                  </th>
                  <th className="px-5 py-3 text-left text-[12px] font-bold uppercase tracking-wider text-slate-500">
                    Phone
                  </th>
                  <th className="px-5 py-3 text-left text-[12px] font-bold uppercase tracking-wider text-slate-500">
                    Status
                  </th>
                  <th className="px-5 py-3 text-left text-[12px] font-bold uppercase tracking-wider text-slate-500">
                    Complete
                  </th>
                  <th className="px-5 py-3 text-left text-[12px] font-bold uppercase tracking-wider text-slate-500">
                    Verified
                  </th>
                  <th className="px-5 py-3 text-left text-[12px] font-bold uppercase tracking-wider text-slate-500">
                    Updated
                  </th>
                  <th className="px-5 py-3 text-right text-[12px] font-bold uppercase tracking-wider text-slate-500">
                    Open
                  </th>
                </tr>
              </thead>

              <tbody className="divide-y divide-slate-200">
                {rows.map((r) => (
                  <tr key={r.registrationNumber} className="hover:bg-slate-50">
                    <td className="px-5 py-4">
                      <div className="text-[13px] font-extrabold text-slate-900 tabular-nums">{r.registrationNumber}</div>
                    </td>
                    <td className="px-5 py-4">
                      <VehicleTypeTag type={r.vehicleType} />
                    </td>
                    <td className="px-5 py-4">
                      <div className="text-[13px] font-medium text-slate-900">{r.ownerName || "—"}</div>
                    </td>
                    <td className="px-5 py-4">
                      <div className="text-[13px] font-medium text-slate-900 tabular-nums">{r.ownerPhone || "—"}</div>
                    </td>
                    <td className="px-5 py-4">
                      <StatusTag status={r.status} />
                    </td>
                    <td className="px-5 py-4">
                      <Tag label={r.complianceComplete ? "YES" : "NO"} tone={r.complianceComplete ? "good" : "neutral"} />
                    </td>
                    <td className="px-5 py-4">
                      <Tag label={r.complianceVerified ? "YES" : "NO"} tone={r.complianceVerified ? "good" : "neutral"} />
                    </td>
                    <td className="px-5 py-4">
                      <div className="text-[13px] font-medium text-slate-700">{fmtDT(r.updatedAt)}</div>
                    </td>
                    <td className="px-5 py-4 text-right">
                      <Button onClick={() => nav(`/admin/compliance/vehicles/${encodeURIComponent(r.registrationNumber)}`)} tone="soft">
                        Open
                      </Button>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}

        {/* ✅ Footer: OpsRides style */}
        <div className="px-5 py-3 border-t border-slate-200 bg-white">
          <div className="flex flex-col gap-3 md:flex-row md:items-center md:justify-between">
            <div className="text-xs text-slate-500">
              {loading ? "Loading…" : total ? `Showing ${pageLabel} of ${total}` : "No records"}
            </div>

            <div className="flex flex-col sm:flex-row sm:items-center gap-3">
              {/* View dropdown */}
              <div className="flex items-center gap-2">
                <div className="text-xs text-slate-500">View</div>

                <div className="relative">
                  <select
                    className="appearance-none h-10 rounded-md border border-slate-200 bg-white pl-3 pr-9 text-sm font-semibold text-slate-700 hover:bg-slate-50 outline-none"
                    value={pageSize}
                    onChange={(e) => {
                      const next = Number(e.target.value);
                      setPageSize(Number.isNaN(next) ? DEFAULT_PAGE_SIZE : next);
                      setPage(0);
                    }}
                  >
                    {PAGE_SIZE_OPTIONS.map((n) => (
                      <option key={n} value={n}>
                        {n}/page
                      </option>
                    ))}
                  </select>

                  <div className="pointer-events-none absolute inset-y-0 right-3 flex items-center text-slate-400">
                    ▼
                  </div>
                </div>
              </div>

              {/* Page label */}
              <div className="text-sm text-slate-700 whitespace-nowrap">
                Page <span className="font-semibold">{page + 1}</span> of{" "}
                <span className="font-semibold">{Math.max(1, totalPages || 1)}</span>
              </div>

              {/* Prev / Next */}
              <div className="flex items-center gap-2">
                <button
                  type="button"
                  className="h-10 px-4 rounded-md border border-slate-200 text-sm font-semibold hover:bg-slate-50 disabled:opacity-50"
                  disabled={disablePrev}
                  onClick={() => setPage((p) => Math.max(0, p - 1))}
                >
                  Previous
                </button>
                <button
                  type="button"
                  className="h-10 px-4 rounded-md border border-slate-200 text-sm font-semibold hover:bg-slate-50 disabled:opacity-50"
                  disabled={disableNext}
                  onClick={() => setPage((p) => Math.min(Math.max(0, totalPages - 1), p + 1))}
                >
                  Next
                </button>
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
}
